import {
  MaterialType,
  ClassificationInputName,
  ClassificationMetadataEntry,
  ClassificationMetadataValueSet
} from '@domain/materials'
import {dataTestId} from '@hconnect/uikit/src/common/utils/testingUtils'
import {useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {ControlledCheckbox} from '../../../../../common/components/form/ControlledCheckbox'
import {MaterialFormDefaultValues} from '../../../materials-details/ExistingMaterialDetails/hooks'

interface ClassificationCheckboxProps<T extends MaterialType> {
  materialType: T
  inputName: ClassificationInputName<T>
  metadata: ClassificationMetadataEntry<T>
  isDisabled: boolean
  customValues?: {trueValue: string; falseValue: string}
  onChangeCb?: () => void
}

export const ClassificationCheckBox = <T extends MaterialType>({
  materialType,
  inputName,
  metadata,
  customValues,
  isDisabled,
  onChangeCb
}: ClassificationCheckboxProps<T>) => {
  const {t} = useTranslation()
  const {control, watch} = useFormContext<MaterialFormDefaultValues>()

  const validValueSet = metadata.inputs[inputName].valueSets.find(
    (valueSet: ClassificationMetadataValueSet) => {
      // Agreement with BE: for now if valuesShownWnen is undefined, it's the only valid value set
      if (valueSet.valuesShownWhen === undefined) {
        return true
      }
      // Agreement with BE: for now any field can be dependant only on one other field
      const conditionForValueSet = valueSet.valuesShownWhen[0]

      const conditionCurrentFormValue = watch(
        `classification.${conditionForValueSet.inputName}`
      ) as string | null
      return conditionCurrentFormValue === conditionForValueSet.value
    }
  )

  const isInputWithNoOptions = !validValueSet?.values.length

  return (
    <ControlledCheckbox
      control={control}
      formDataName={`classification.${inputName}`}
      label={t(`materialsSettings.classificationInputs.${materialType}.${inputName}`)}
      rules={{
        validate: (value) => isInputWithNoOptions || !!value || t('error.required')
      }}
      disabled={isDisabled || isInputWithNoOptions}
      formatValue={
        customValues
          ? (checked) => (checked ? customValues.trueValue : customValues.falseValue)
          : undefined
      }
      parseValue={customValues ? (value) => value === customValues.trueValue : undefined}
      onChangeCb={onChangeCb}
      {...dataTestId(`${materialType}_classification_${inputName}`)}
    />
  )
}
